import React, { useEffect, useRef, useState } from "react";
import { Paperclip, Plus, X, FileText, File as FileIcon, Loader } from "lucide-react";
import GenericModal from "components/Modal";

// ─── Types ─────────────────────────────────────────────────────────────────────

/**
 * One attachment belonging to a single expense row.
 * Mirrors the previous single-file fields (file / s3Key / uploading / uploadError)
 * but now lives inside a per-expense `files` array so each expense can hold many.
 */
export interface AttachedFile {
  id: string;
  file: File;
  // Set after the eager S3 upload resolves. Only used in S3 storage mode.
  s3Key: string | null;
  uploading: boolean;
  uploadError: string;
}

interface ExpenseAttachmentsProps {
  files: AttachedFile[];
  /** "S3" | "DRIVE" — drives the "Uploaded" status label only; upload itself is owned by the parent. */
  storageType: string;
  maxFiles: number;
  /** `accept` attribute forwarded to the hidden file input. */
  accept: string;
  /** Row-level validation / upload error shown under the summary button. */
  errorText?: string;
  /** Helper text shown under the summary button when there is no error and no file. */
  helpText?: string;
  onAddFiles: (files: FileList) => void;
  onRemoveFile: (fileId: string) => void;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * Compact thumbnail: a real preview for images (via an object URL that is
 * revoked on unmount) and a type icon for PDFs / other documents.
 * Kept small so it never enlarges the row or the popover.
 */
const FileThumb = ({ file }: { file: File }) => {
  const isImage = file.type.startsWith("image/");
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!isImage) return;
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file, isImage]);

  const box =
    "w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 flex items-center justify-center bg-slate-100 dark:bg-slate-700";

  if (isImage && url) {
    return (
      <div className={box}>
        <img src={url} alt="" className="w-full h-full object-cover" />
      </div>
    );
  }

  const isPdf = file.type === "application/pdf";
  return (
    <div className={box}>
      {isPdf ? (
        <FileText size={18} className="text-red-400" />
      ) : (
        <FileIcon size={18} className="text-slate-400" />
      )}
    </div>
  );
};

// ─── Component ─────────────────────────────────────────────────────────────────

/**
 * Per-expense attachment manager.
 *
 * Renders a compact summary button ("📎 Add files" / "📎 N file(s)") that keeps
 * the reimbursement table narrow, and a modal (reusing GenericModal so it portals
 * out of the table's overflow) to preview, add and remove individual files.
 *
 * State is owned by the parent (controlled component) so every expense keeps its
 * own attachment list — this component never holds the file array itself.
 */
const ExpenseAttachments: React.FC<ExpenseAttachmentsProps> = ({
  files,
  storageType,
  maxFiles,
  accept,
  errorText,
  helpText,
  onAddFiles,
  onRemoveFile,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const count = files.length;
  const anyUploading = files.some((f) => f.uploading);
  const atMax = count >= maxFiles;

  const openPicker = () => inputRef.current?.click();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length) onAddFiles(e.target.files);
    // Reset so re-selecting the same file fires onChange again.
    e.target.value = "";
  };

  // Summary button visual state — mirrors the existing attach-button styling.
  const stateCls = errorText
    ? "border-error/40 bg-error/10 text-error"
    : anyUploading
      ? "border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-400"
      : count > 0
        ? "border-success/40 bg-success/10 text-success"
        : "border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600";

  const summaryLabel =
    count === 0 ? "Add files" : `${count} file${count > 1 ? "s" : ""}`;

  return (
    <>
      {count === 0 ? (
        // Empty state — a single full-width button that opens the file picker.
        <button
          type="button"
          onClick={openPicker}
          aria-label="Add attachments"
          className={`inline-flex items-center gap-1.5 w-full px-3 py-3 rounded-lg border text-xs font-medium transition-colors whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-blue-500/40 ${stateCls}`}
        >
          {anyUploading ? (
            <Loader size={14} className="flex-shrink-0 animate-spin" />
          ) : (
            <Paperclip size={14} className="flex-shrink-0" />
          )}
          {summaryLabel}
        </button>
      ) : (
        // Has files — count on the left (opens the manage modal),
        // a "+" add-more action on the right (opens the picker).
        <div className={`inline-flex items-stretch w-full rounded-lg border overflow-hidden ${stateCls}`}>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            aria-label={`Manage ${count} attachment${count > 1 ? "s" : ""}`}
            className="inline-flex items-center gap-1.5 flex-1 min-w-0 px-3 py-3 text-xs font-medium whitespace-nowrap transition-colors focus:outline-none hover:bg-black/5 dark:hover:bg-white/5"
          >
            {anyUploading ? (
              <Loader size={14} className="flex-shrink-0 animate-spin" />
            ) : (
              <Paperclip size={14} className="flex-shrink-0" />
            )}
            {summaryLabel}
          </button>

          {/* Divider inherits the current text color so it matches each state. */}
          <span className="w-px self-stretch bg-current opacity-20" aria-hidden="true" />

          <button
            type="button"
            onClick={openPicker}
            disabled={atMax}
            aria-label="Add more files"
            title={atMax ? `Maximum ${maxFiles} files` : "Add more files"}
            className="inline-flex items-center justify-center px-2.5 py-3 transition-colors focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed hover:bg-black/5 dark:hover:bg-white/5"
          >
            <Plus size={16} className="flex-shrink-0" />
          </button>
        </div>
      )}

      {/* Reserved height matches the other cells' error slot so rows stay aligned. */}
      <div className="h-4 mt-0.5">
        {errorText ? (
          <p className="text-xs font-medium leading-tight whitespace-nowrap text-error">{errorText}</p>
        ) : count === 0 && helpText ? (
          <p className="text-xs leading-tight whitespace-nowrap text-gray-400 dark:text-gray-500">{helpText}</p>
        ) : null}
      </div>

      {/* Hidden multi-file input reused by both the summary button and "Add more files". */}
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        className="sr-only"
        onChange={handleInputChange}
      />

      <GenericModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        size="sm"
        customWidth="380px"
      >
        {/* Header: title + live count badge */}
        <div className="flex items-center gap-2 mb-3 -mt-1">
          <h3 className="text-base font-semibold text-slate-900 dark:text-white">Attachments</h3>
          <span className="px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 text-xs font-semibold tabular-nums">
            {count}
          </span>
        </div>

        {count === 0 ? (
          <p className="text-sm text-gray-400 py-6 text-center">No files attached yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-700">
            {files.map((f) => (
              <li key={f.id} className="flex items-center gap-3 py-2.5">
                <FileThumb file={f.file} />
                <div className="min-w-0 flex-1">
                  <p
                    className="text-sm text-slate-700 dark:text-slate-200 truncate"
                    title={f.file.name}
                  >
                    {f.file.name}
                  </p>
                  {f.uploading ? (
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <Loader size={12} className="animate-spin" /> Uploading…
                    </span>
                  ) : f.uploadError ? (
                    <span className="text-xs text-error">{f.uploadError}</span>
                  ) : storageType === "S3" && f.s3Key ? (
                    <span className="text-xs text-success">Uploaded</span>
                  ) : (
                    <span className="text-xs text-gray-400">{formatSize(f.file.size)}</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onRemoveFile(f.id)}
                  aria-label={`Remove ${f.file.name}`}
                  title={`Remove ${f.file.name}`}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-error hover:bg-error/10 transition-colors flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-error/40"
                >
                  <X size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Footer: add more (disabled at the limit) + count indicator */}
        <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
          <button
            type="button"
            onClick={openPicker}
            disabled={atMax}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:opacity-75 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity focus:outline-none"
          >
            <Plus size={16} /> Add more files
          </button>
          <span className="text-xs text-gray-400 tabular-nums">
            {count} / {maxFiles}
          </span>
        </div>
      </GenericModal>
    </>
  );
};

export default ExpenseAttachments;
